import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { viewQueryKeys } from "@/shared/domain/views/hooks";
import { usePublishToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  RESOURCE_TABLE_ACTION_TOPIC,
  type ResourceTableActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";

import type { AgentThreadRecord } from "../AgentThreadRecord";
import { serverViewWritesIn } from "../serverViewWrites";

/**
 * Bring the page up to date with Views the agent changed on the server.
 *
 * `create_view`, `update_view` and `delete_view` run in the backend, so none of
 * the View mutation hooks saw them and nothing else refreshes what they touched.
 * For each write this finds in the thread, it does what the matching picker
 * mutation does: every write marks the org's View queries stale, and a delete
 * also drops the View's cached record and tells the list on screen, which
 * unloads the View if it is the one loaded.
 *
 * Each write is handled once per mount, by `tool_use_id`, however often the
 * thread is re-polled. Opening a thread handles the writes already in its
 * history too; that is harmless, since a View deleted earlier cannot be the
 * one loaded now, and costs one refetch of the View lists.
 */
export function useServerViewWrites(
  thread: AgentThreadRecord | undefined,
  orgId: string | undefined,
): void {
  const queryClient = useQueryClient();
  const publish =
    usePublishToAiAgentThreadPubSubBus<ResourceTableActionPayload>(
      RESOURCE_TABLE_ACTION_TOPIC,
    );
  const handled = useRef(new Set<string>());

  useEffect(() => {
    if (thread === undefined || orgId === undefined) {
      return;
    }
    const fresh = serverViewWritesIn(thread).filter(
      (write) => !handled.current.has(write.toolUseId),
    );
    if (fresh.length === 0) {
      return;
    }

    for (const write of fresh) {
      handled.current.add(write.toolUseId);
      if (write.kind === "delete") {
        queryClient.removeQueries({
          queryKey: viewQueryKeys.id(orgId, write.viewId),
        });
        // `false` means no list is mounted, so no View is loaded to unload.
        publish({ type: "view_deleted", payload: { viewId: write.viewId } });
      }
    }
    void queryClient.invalidateQueries({ queryKey: viewQueryKeys.all(orgId) });
  }, [thread, orgId, queryClient, publish]);
}
